import { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Column } from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { faBarsFilter, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportDateRange } from 'assets/js/pages/reports/ReportDateRange';
import { queryKeyStrategy } from 'assets/js/pages/reports/queryKeyStrategy';
import { ClearFiltersButton } from 'components/filter/ClearFiltersButton';
import { Filter } from 'components/filter/filters-next/Filter';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterListItem } from 'components/filter/filters-next/FilterListItem';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { prefixDynamicFilter } from 'components/filter/filters-next/util';
import { ServerKey } from 'components/filter/types';
import { TableContext } from 'components/table/TableContext';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { getTableResourceFromReportResource } from 'components/table/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { useReportSchemas } from 'customHooks/useReportSchemas';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';
import { ColumnSchema } from 'types/domain';

const REFRESH_DEBOUNCE_INTERVAL = 1000;

interface DynamicColumnProps {
    columns: ColumnSchema[];
    tableColumn: Column<any>;
}

const ReportFilterBar = () => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { table } = useContext(TableContext);
    const { isPending: isLoadingReport, data: report } = useCurrentReport();
    const { data: schemas } = useReportSchemas();
    const { currentGroup } = useFiltersByKey('singleReport');
    const { query } = useContext(TableCoreStateContext);
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingFromServer = query?.isRefetching || isLoadingReport;
    const queryKey = queryKeyStrategy(report.resource);
    const contactColumns =
        schemas.find(schema => schema.resource === 'contacts')?.schema.columns ?? [];
    const transactionColumns =
        schemas.find(schema => schema.resource === 'transactions')?.schema.columns ?? [];
    const flatColumns = table
        .getAllFlatColumns()
        .filter(col => col.columnDef.meta?.dynamic && col.columnDef.meta?.filterable);

    const DynamicColumn = useCallback((props: DynamicColumnProps) => {
        const { columns, tableColumn } = props;
        const filter = columns.find(schema => schema.name === tableColumn.id);
        const isHidden = !tableColumn.getIsVisible() && !!tableColumn.columnDef.meta?.aggregate;

        if (!filter || isHidden) {
            return null;
        }

        return (
            <FilterListItem
                serverKey={prefixDynamicFilter(filter.name as ServerKey)}
                isLegacy={false}
            />
        );
    }, []);

    useEffect(() => {
        if (isLoadingFromServer) {
            setIsLoading(true);
        }
    }, [isLoadingFromServer]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isLoadingFromServer) {
                setIsLoading(true);
            } else {
                setIsLoading(false);
            }
        }, REFRESH_DEBOUNCE_INTERVAL);

        return () => {
            clearTimeout(timeout);
        };
    }, [isLoadingFromServer]);

    return (
        <FilterGroupProvider showInnerOperator={false} showOuterOperator={false}>
            <div
                className={
                    'flex flex-wrap-reverse items-center justify-between gap-x-12 gap-y-4 md:flex-nowrap'
                }
            >
                <div className={'flex flex-col gap-2'}>
                    <ReportDateRange />
                    {currentGroup && currentGroup.filters.length > 0 && (
                        <div className={'flex flex-wrap gap-1.5'}>
                            <QuickFiltersBar />
                            <ClearFiltersButton />
                        </div>
                    )}
                </div>
                <div className={'flex gap-1.5'}>
                    <Filter
                        addGroup={false}
                        resource={getTableResourceFromReportResource(report.resource)}
                        cacheKey={'singleReport'}
                        noIcon={true}
                        variant={'button'}
                        popoverContentProps={{
                            align: 'end',
                        }}
                        dynamicFilters={{
                            contact:
                                report.resource === 'contacts' &&
                                flatColumns.map(col => (
                                    <DynamicColumn
                                        key={col.id}
                                        columns={contactColumns}
                                        tableColumn={col}
                                    />
                                )),
                            transaction:
                                report.resource === 'transactions' &&
                                flatColumns.map(col => (
                                    <DynamicColumn
                                        key={col.id}
                                        columns={transactionColumns}
                                        tableColumn={col}
                                    />
                                )),
                        }}
                    >
                        <div className={'flex items-center gap-1.5 text-gray-900'}>
                            <FontAwesomeIcon icon={faBarsFilter} />
                            Filter
                        </div>
                    </Filter>
                    <Button
                        disabled={isLoading}
                        variant={'outline'}
                        color={'neutral'}
                        shape={'square'}
                        onClick={() => {
                            queryClient.invalidateQueries({
                                queryKey: queryKey(accountId),
                                exact: false,
                            });
                            queryClient.invalidateQueries({
                                queryKey: accountQueryKeys.reportStats(accountId, report.id),
                                exact: false,
                            });
                        }}
                        aria-label="Refresh"
                    >
                        <FontAwesomeIcon
                            className={twJoin(isLoading && 'animate-spin')}
                            icon={faRefresh}
                        />
                    </Button>
                </div>
            </div>
        </FilterGroupProvider>
    );
};

export { ReportFilterBar };
